import { type McpAuthorizeRequest, mcpClientsUtils } from 'app-components/flux/mcp-clients';
import { getLogger } from 'app-components/utils/core';

const logger = getLogger('utils/mcp/pendingAuthorize');

export const MCP_PENDING_AUTHORIZE_KEY = 'mcpPendingAuthorize';
const SESSION_STATES_KEY = 'sessionStates';
const CONNECTED_AI_LANDING_KEY = 'mcpConnectedAiLanding';
const MCP_PENDING_AUTHORIZE_TTL_MS = 10 * 60 * 1000;

type StashedMcpAuthorize = McpAuthorizeRequest & { stashedAt: number };

const readSessionStates = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(SESSION_STATES_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch (e) {
    logger.error('failed to read session states', e);
    return [];
  }
};

export const hasRememberedSessionState = (sessionState: string): boolean =>
  new Set(readSessionStates()).has(sessionState);

export const forgetSessionState = (sessionState: string): void => {
  const remaining = readSessionStates().filter((stored) => stored !== sessionState);
  try {
    localStorage.setItem(SESSION_STATES_KEY, JSON.stringify(remaining));
  } catch (e) {
    logger.error('failed to forget session state', e);
  }
};

export const setPendingMcpAuthorize = (request: McpAuthorizeRequest): boolean => {
  try {
    const stashed: StashedMcpAuthorize = { ...request, stashedAt: Date.now() };
    sessionStorage.setItem(MCP_PENDING_AUTHORIZE_KEY, JSON.stringify(stashed));
    return true;
  } catch (e) {
    logger.error('failed to stash pending mcp authorize', e);
    return false;
  }
};

export const readPendingMcpAuthorize = (): McpAuthorizeRequest | null => {
  try {
    const raw = sessionStorage.getItem(MCP_PENDING_AUTHORIZE_KEY);
    if (!raw) {
      return null;
    }
    const { stashedAt, ...stored } = JSON.parse(raw) as Partial<StashedMcpAuthorize>;
    const request = mcpClientsUtils.parseMcpAuthorizeRequest(stored);
    if (!request) {
      return null;
    }
    if (!stashedAt || Date.now() - stashedAt > MCP_PENDING_AUTHORIZE_TTL_MS) {
      logger.debug('ignoring a stale pending mcp authorize', { clientId: request.clientId });
      return null;
    }
    return request;
  } catch (e) {
    logger.error('failed to read pending mcp authorize', e);
    return null;
  }
};

export const clearPendingMcpAuthorize = (): void => {
  try {
    sessionStorage.removeItem(MCP_PENDING_AUTHORIZE_KEY);
  } catch (e) {
    logger.error('failed to clear pending mcp authorize', e);
  }
};

export const setConnectedAiLanding = (clientId: string): void => {
  try {
    sessionStorage.setItem(CONNECTED_AI_LANDING_KEY, clientId);
  } catch (e) {
    logger.error('failed to flag the connected ai landing', e);
  }
};

export const consumeConnectedAiLanding = (): string | null => {
  try {
    const clientId = sessionStorage.getItem(CONNECTED_AI_LANDING_KEY);
    if (!clientId) {
      return null;
    }
    sessionStorage.removeItem(CONNECTED_AI_LANDING_KEY);
    return clientId;
  } catch (e) {
    logger.error('failed to read the connected ai landing', e);
    return null;
  }
};
